import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Terminal from '../components/terminal/Terminal';

/**
 * Standalone terminal page for opening terminal in a new browser window
 */
const StandaloneTerminalPage: React.FC = () => {
  const location = useLocation();
  const [terminalParams, setTerminalParams] = useState<{
    sessionId: string;
    instanceId?: string;
    provider?: 'aws' | 'gcp' | 'azure';
    instanceType?: string;
    region?: string;
    createNewSession?: boolean;
  } | null>(null);

  // First effect: Parse URL params (only when location changes)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('sessionId');
    
    if (sessionId) {
      setTerminalParams({
        sessionId,
        instanceId: params.get('instanceId') || undefined,
        provider: (params.get('provider') as 'aws' | 'gcp' | 'azure') || 'aws',
        instanceType: params.get('instanceType') || undefined,
        region: params.get('region') || undefined,
        createNewSession: params.get('createNewSession') === 'true'
      });
    }
  }, [location.search]); // Only depend on location.search

  // Second effect: Handle window title and cleanup
  useEffect(() => {
    if (!terminalParams) return;
    
    // Set window title
    document.title = `Terminal - ${terminalParams.sessionId?.slice(0, 8) || 'PropAlgos'}`;
    
    // Handle window close cleanup
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to close the terminal?';
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [terminalParams]); // This can safely depend on terminalParams

  if (!terminalParams) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid Terminal Session</h1>
          <p className="text-gray-400">No session ID provided</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-black flex flex-col overflow-hidden">
      {/* Minimal header */}
      <div className="h-10 bg-black/80 backdrop-blur-sm border-b border-gray-800/50 flex items-center px-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50" />
          <span className="text-sm text-gray-300 font-medium">Terminal Session</span>
          <span className="text-xs text-gray-500">{terminalParams.sessionId.slice(0, 8)}...</span>
        </div>
        <div className="ml-auto flex items-center space-x-4 text-xs text-gray-500">
          <span className="px-2 py-1 bg-gray-800/50 rounded">
            {terminalParams.provider?.toUpperCase()}
          </span>
          {terminalParams.instanceType && (
            <span>{terminalParams.instanceType}</span>
          )}
          {terminalParams.region && (
            <span>{terminalParams.region}</span>
          )}
          <button
            onClick={() => {
              window.close();
              setTimeout(() => {
                window.location.href = '/dashboard/compute';
              }, 100);
            }}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all rounded"
            title="Close Window"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Terminal in fullscreen mode */}
      <div className="flex-1 relative">
        <Terminal
          sessionId={terminalParams.sessionId}
          instanceId={terminalParams.instanceId}
          provider={terminalParams.provider}
          instanceType={terminalParams.instanceType}
          region={terminalParams.region}
          standalone={true}
          onClose={() => {
            window.close();
            setTimeout(() => {
              window.location.href = '/dashboard/compute';
            }, 100);
          }}
        />
      </div>
    </div>
  );
};

export default StandaloneTerminalPage;